// Column definitions for the Rufqa report. The order here is the table and
// Excel column order, and must match OMS.Models.DisplayMembersReportColumnModel.

export const COLUMN_GROUPS = [
  {
    key: "basic",
    label: "member_report_group_basic",
    columns: ["id", "serialNumber", "name", "registrationNumber", "fatherName", "dateOfBirth", "cnic"],
  },
  {
    key: "dates",
    label: "member_report_group_dates",
    columns: ["dateOfRegistration", "dateOfFirstBait", "dateOfSecondBait", "dateOfThirdBait", "dateOfMultazim"],
  },
  {
    key: "unit",
    label: "member_report_group_unit",
    columns: ["country", "city", "halqa", "tanzeem", "usra", "nazam", "photo", "memberType", "responsbilities"],
  },
  {
    key: "contact",
    label: "member_report_group_contact",
    columns: ["emailAndPhoneNumber", "currentAddress", "permanentAddress", "nativeTown"],
  },
  {
    key: "personal",
    label: "member_report_group_personal",
    columns: [
      "maritalStatus",
      "childrenDetails",
      "bloodGroup",
      "education",
      "educationDiscipline",
      "occupation",
      "experience",
      "skill",
    ],
  },
  {
    key: "nisaab",
    label: "member_report_group_nisaab",
    columns: ["mubtadiNisaab", "multazimNisaab", "zimedaraanNisaab", "mudariseenNisaab", "dawatiNisaab"],
  },
  {
    key: "courses",
    label: "member_report_group_courses",
    columns: [
      "mubtadiTarbiyatiCourse",
      "multazimTarbiyatiCourse",
      "nuqabaCourse",
      "mudariseenCourse",
      "mudariseenRefresherCourse",
    ],
  },
  {
    key: "mudaris",
    label: "member_report_group_mudaris",
    columns: ["tajweedTest", "arabicTest", "registeredMudaris", "acceptedMudaris", "markaziMudaris"],
  },
];

export const COLUMN_ORDER = [
  "id",
  "serialNumber",
  "name",
  "registrationNumber",
  "fatherName",
  "dateOfBirth",
  "cnic",
  "dateOfRegistration",
  "dateOfFirstBait",
  "dateOfSecondBait",
  "dateOfThirdBait",
  "country",
  "city",
  "halqa",
  "tanzeem",
  "usra",
  "nazam",
  "photo",
  "memberType",
  "responsbilities",
  "emailAndPhoneNumber",
  "currentAddress",
  "permanentAddress",
  "nativeTown",
  "bloodGroup",
  "maritalStatus",
  "childrenDetails",
  "education",
  "educationDiscipline",
  "occupation",
  "experience",
  "skill",
  "dateOfMultazim",
  "mubtadiNisaab",
  "multazimNisaab",
  "zimedaraanNisaab",
  "mudariseenNisaab",
  "dawatiNisaab",
  "mubtadiTarbiyatiCourse",
  "multazimTarbiyatiCourse",
  "nuqabaCourse",
  "mudariseenCourse",
  "mudariseenRefresherCourse",
  "tajweedTest",
  "arabicTest",
  "registeredMudaris",
  "acceptedMudaris",
  "markaziMudaris",
];

export const NEW_COLUMNS = [];

export const BOOLEAN_COLUMNS = [
  "mubtadiNisaab",
  "multazimNisaab",
  "zimedaraanNisaab",
  "mudariseenNisaab",
  "dawatiNisaab",
  "registeredMudaris",
  "acceptedMudaris",
  "markaziMudaris",
];

export const DATE_COLUMNS = [
  "dateOfBirth",
  "dateOfRegistration",
  "dateOfFirstBait",
  "dateOfSecondBait",
  "dateOfThirdBait",
  "dateOfMultazim",
  "mubtadiTarbiyatiCourse",
  "multazimTarbiyatiCourse",
  "nuqabaCourse",
  "mudariseenCourse",
  "mudariseenRefresherCourse",
];

// Columns whose header sorts the report on the server (unchanged from before).
export const SORTABLE_COLUMNS = ["registrationNumber", "name", "dateOfRegistration", "memberType", "responsbilities"];

const DEFAULT_COLUMNS = [
  "registrationNumber",
  "name",
  "fatherName",
  "halqa",
  "nazam",
  "memberType",
  "responsbilities",
  "currentAddress",
];

export const COLUMN_PRESETS = [
  { key: "default", label: "member_report_preset_default", columns: DEFAULT_COLUMNS },
  {
    key: "contact",
    label: "member_report_preset_contact",
    columns: ["name", "registrationNumber", "fatherName", "nazam", "emailAndPhoneNumber", "currentAddress"],
  },
  {
    key: "unit",
    label: "member_report_preset_unit",
    columns: ["name", "registrationNumber", "halqa", "tanzeem", "usra", "nazam", "memberType", "responsbilities"],
  },
  {
    key: "tarbiyat",
    label: "member_report_preset_tarbiyat",
    columns: [
      "name",
      "registrationNumber",
      "memberType",
      ...COLUMN_GROUPS.find((g) => g.key === "nisaab").columns,
      ...COLUMN_GROUPS.find((g) => g.key === "courses").columns,
    ],
  },
  {
    key: "mudariseen",
    label: "member_report_preset_mudariseen",
    columns: [
      "name",
      "registrationNumber",
      "nazam",
      "mudariseenNisaab",
      "mudariseenCourse",
      "mudariseenRefresherCourse",
      ...COLUMN_GROUPS.find((g) => g.key === "mudaris").columns,
    ],
  },
];

export const columnLabelKey = (column) => "member_report_member_" + column;

export const toColumnModel = (selectedColumns) =>
  COLUMN_ORDER.reduce((model, column) => ({ ...model, [column]: selectedColumns.includes(column) }), {});

export const getDefaultColumns = () => [...DEFAULT_COLUMNS];

export const matchingPreset = (selectedColumns) =>
  COLUMN_PRESETS.find(
    (p) => p.columns.length === selectedColumns.length && p.columns.every((c) => selectedColumns.includes(c))
  )?.key;

// Column choice can be remembered per browser. Storage may be unavailable, so every access is guarded.
const STORAGE_KEY = "memberReport.columns";

export const loadRememberedColumns = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
    if (Array.isArray(stored)) return stored.filter((c) => COLUMN_ORDER.includes(c));
  } catch (e) {}
  return null;
};

export const saveRememberedColumns = (selectedColumns, remember) => {
  try {
    if (remember) localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedColumns));
    else localStorage.removeItem(STORAGE_KEY);
  } catch (e) {}
};
